import { enableMetricGroupReleasePolicyConnections } from '@gonfalon/dogfood-flags/dogfood-flags';
import { getMetricGroupQuery } from '@gonfalon/rest-api/queries/metricGroups';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { useQueries } from '@tanstack/react-query';

import type { MetricGroupRep } from './types';

export function useMetricGroupsByKeys(keys: string[], options?: { staleTime?: number }) {
  const projectKey = useProjectKey();
  const includeReleasePolicyCount = enableMetricGroupReleasePolicyConnections();

  const queries = useQueries({
    queries: keys.map((metricGroupKey) => ({
      ...getMetricGroupQuery({
        projectKey,
        metricGroupKey,
        query: {
          expand: [
            'experimentCount',
            'guardedRolloutCount',
            ...(includeReleasePolicyCount ? (['releasePolicyCount'] as const) : []),
          ],
        },
      }),
      ...(options?.staleTime !== undefined && { staleTime: options.staleTime }),
    })),
  });

  const isPending = queries.some((query) => query.isPending);
  const isFetching = queries.some((query) => query.isFetching);
  const isError = queries.some((query) => query.isError);
  if (isPending) {
    return {
      metricGroups: undefined,
      isPending,
      isFetching,
      isError,
    };
  }

  const metricGroups = queries
    .map((query) => query.data)
    .filter((metricGroup): metricGroup is MetricGroupRep => metricGroup !== undefined);

  return {
    metricGroups,
    isPending,
    isFetching,
    isError,
  };
}
